import posthog, { posthogEnabled } from './posthog';

// Thin analytics facade over PostHog so the rest of the app has one place to
// identify users, capture events, and reset on logout. All calls are safe
// no-ops when PostHog isn't configured (no REACT_APP_POSTHOG_KEY).

export type AppUser = {
  userId?: string | null;
  email?: string | null;
  username?: string | null;
  userRole?: string | null;
  appversion?: string | null;
  lastLogin?: string | null;
};

// Person properties attached to the user's PostHog profile + every event and
// session replay, so all activity is tied to a real person.
const toPersonProps = (user: AppUser) => ({
  name: user.username || undefined, // full name (falls back to username)
  username: user.username || undefined,
  email: user.email || undefined,
  role: user.userRole ?? undefined,
  app_version: user.appversion || undefined,
  last_login: user.lastLogin || undefined
});

// Identify the signed-in user. distinct_id is the stable userId.
export const identifyUser = (user: AppUser) => {
  if (!posthogEnabled || !user?.userId) return;
  posthog.identify(String(user.userId), toPersonProps(user));
};

// Re-identify from localStorage whenever a signed-in user re-opens the app,
// so returning sessions are attributed without needing a fresh login.
export const identifyFromStorage = () => {
  const userId = localStorage.getItem('userId');
  const token = localStorage.getItem('token');
  if (!posthogEnabled || !userId || !token) return;
  identifyUser({
    userId,
    email: localStorage.getItem('email'),
    username: localStorage.getItem('username'),
    userRole: localStorage.getItem('userRole'),
    appversion: localStorage.getItem('appversion'),
    lastLogin: localStorage.getItem('lastLogin')
  });
};

export const captureEvent = (
  event: string,
  properties?: Record<string, any>
) => {
  if (!posthogEnabled) return;
  posthog.capture(event, properties);
};

// Why a logout happened — lets us tell a deliberate sign-out from an
// expired/invalid session.
export type LogoutReason = 'manual' | 'idle_timeout' | 'session_expired';

export const captureLogout = (reason: LogoutReason) => {
  if (!posthogEnabled) return;
  posthog.capture('user_logged_out', { reason });
  // Stop tying subsequent (anonymous) activity to this person.
  posthog.reset();
};

export default posthog;
